/**
 * 🧪 API Test Page - CRUD Operations Demo
 * 
 * Componente para demonstrar todas as operações CRUD funcionando
 * com nossa API REST integrada ao frontend
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, CheckCircle, Loader2, Plus, Trash2, Edit, Users } from 'lucide-react';

import { 
  useUsers, 
  useCreateUser, 
  useUpdateUser, 
  useDeleteUser,
  useEvents,
  useCreateEvent,
  useParticipants,
  useCreateParticipant,
} from '@/hooks/useApi';

import type { CreateUserData, CreateEventData, CreateParticipantData } from '@/services/api';

export default function ApiTestPage() {
  const [selectedTab, setSelectedTab] = useState<'users' | 'events' | 'participants'>('users');

  // Users State
  const { data: users, isLoading: usersLoading, error: usersError } = useUsers();
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();

  // Events State  
  const { data: events, isLoading: eventsLoading } = useEvents();
  const createEventMutation = useCreateEvent();

  // Participants State
  const { data: participants, isLoading: participantsLoading } = useParticipants();
  const createParticipantMutation = useCreateParticipant();

  // Form States
  const [newUser, setNewUser] = useState<CreateUserData>({
    name: '',
    email: '',
    role: 'REVENDA',
    companyName: '',
    companyType: '',
  });

  const [newEvent, setNewEvent] = useState<CreateEventData>({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    location: '',
    organizerId: '',
  });

  const [newParticipant, setNewParticipant] = useState<CreateParticipantData>({
    name: '',
    email: '',
    companyName: '',
    companyType: '',
    role: 'FABRICANTE',
    eventId: '',
  });

  // Handlers
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createUserMutation.mutateAsync(newUser);
      setNewUser({ name: '', email: '', role: 'REVENDA', companyName: '', companyType: '' });
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Tem certeza que deseja deletar este usuário?')) {
      try {
        await deleteUserMutation.mutateAsync(userId);
      } catch (error) {
        console.error('Erro ao deletar usuário:', error);
      }
    }
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createEventMutation.mutateAsync(newEvent);
      setNewEvent({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        location: '',
        organizerId: '',
      });
    } catch (error) {
      console.error('Erro ao criar evento:', error);
    }
  };

  const handleCreateParticipant = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createParticipantMutation.mutateAsync(newParticipant);
      setNewParticipant({
        name: '',
        email: '',
        companyName: '',
        companyType: '',
        role: 'FABRICANTE',
        eventId: '',
      });
    } catch (error) {
      console.error('Erro ao criar participante:', error);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">API Test Suite</h1>
        <p className="text-muted-foreground">
          Demonstração das operações CRUD funcionando com nossa API REST
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-lg border p-1 bg-muted">
          <button
            onClick={() => setSelectedTab('users')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedTab === 'users' 
                ? 'bg-background text-foreground shadow-sm' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            👥 Usuários
          </button>
          <button
            onClick={() => setSelectedTab('events')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedTab === 'events' 
                ? 'bg-background text-foreground shadow-sm' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            📅 Eventos
          </button>
          <button
            onClick={() => setSelectedTab('participants')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedTab === 'participants' 
                ? 'bg-background text-foreground shadow-sm' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            🏢 Participantes
          </button>
        </div>
      </div>

      {/* Users Tab */}
      {selectedTab === 'users' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Create User Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Criar Novo Usuário
              </CardTitle>
              <CardDescription>
                Adicione um novo usuário ao sistema
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <Label htmlFor="user-name">Nome</Label>
                  <Input
                    id="user-name"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="Nome do usuário"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="user-email">Email</Label>
                  <Input
                    id="user-email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="email@exemplo.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="user-role">Papel</Label>
                  <select
                    id="user-role"
                    className="w-full p-2 border border-input bg-background rounded-md"
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value as any })}
                  >
                    <option value="REVENDA">REVENDA</option>
                    <option value="FABRICANTE">FABRICANTE</option>
                    <option value="PRODUCAO">PRODUCAO</option>
                    <option value="AGIS">AGIS</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="user-company">Empresa</Label>
                  <Input
                    id="user-company"
                    value={newUser.companyName || ''}
                    onChange={(e) => setNewUser({ ...newUser, companyName: e.target.value })}
                    placeholder="Nome da empresa"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={createUserMutation.isPending}
                >
                  {createUserMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    'Criar Usuário'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Users List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Lista de Usuários
              </CardTitle>
              <CardDescription>
                {usersLoading ? 'Carregando...' : `${users?.length || 0} usuários encontrados`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : usersError ? (
                <div className="flex items-center gap-2 text-red-600 py-8">
                  <AlertCircle className="h-5 w-5" />
                  Erro ao carregar usuários
                </div>
              ) : users && users.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium">{user.nome}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                        <Badge variant="outline" className="mt-1">
                          {user.papel}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={deleteUserMutation.isPending}
                        >
                          {deleteUserMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum usuário encontrado
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Events Tab */}
      {selectedTab === 'events' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Create Event Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Criar Novo Evento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateEvent} className="space-y-4">
                <div>
                  <Label htmlFor="event-name">Nome do Evento</Label>
                  <Input
                    id="event-name"
                    value={newEvent.name}
                    onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                    placeholder="Nome do evento"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="event-description">Descrição</Label>
                  <Textarea
                    id="event-description"
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    placeholder="Descrição do evento"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="event-start">Data Início</Label>
                    <Input
                      id="event-start"
                      type="datetime-local"
                      value={newEvent.startDate}
                      onChange={(e) => setNewEvent({ ...newEvent, startDate: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="event-end">Data Fim</Label>
                    <Input
                      id="event-end"
                      type="datetime-local"
                      value={newEvent.endDate}
                      onChange={(e) => setNewEvent({ ...newEvent, endDate: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="event-location">Local</Label>
                  <Input
                    id="event-location"
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                    placeholder="Local do evento"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={createEventMutation.isPending}
                >
                  {createEventMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    'Criar Evento'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Events List */}
          <Card>
            <CardHeader>
              <CardTitle>Lista de Eventos</CardTitle>
              <CardDescription>
                {eventsLoading ? 'Carregando...' : `${events?.length || 0} eventos encontrados`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {eventsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : events && events.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {events.map((event) => (
                    <div key={event.id} className="p-3 border rounded-lg">
                      <h4 className="font-medium">{event.nome}</h4>
                      <p className="text-sm text-muted-foreground">
                        Criado em {new Date(event.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline">
                          {event.diasEvento?.length || 0} dias
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum evento encontrado
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Success/Error Status */}
      {(createUserMutation.isSuccess || createEventMutation.isSuccess || createParticipantMutation.isSuccess) && (
        <div className="fixed bottom-4 right-4">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Operação realizada com sucesso!</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}